import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Formik } from 'formik';

import { SingleSelect } from '@/common';
import { FilterModalLayout, FilterPopUpValues } from '@/components/layouts';
import { PAGE_SIZE } from '@/constants';
import { AuthContext } from '@/contexts';
import { getEnabledStatusFilterOptions, getOnlineStatusFilterOptions } from '@/helpers';
import { useGetAllOrganizations } from '@/services';
import { useFilterStore } from '@/store';
import { FilterDevicesProps, Organization, SelectOptionProps } from '@/types';

export const FilterDevices = ({
  onClose,
  filterByOnlineStatus,
  filterByEnabledStatus,
  filterByOrganization,
  apply,
  reset
}: FilterDevicesProps) => {
  const [t] = useTranslation('common');
  const { currentUserData: currentUser } = useContext(AuthContext);
  const { organizationFilter } = useFilterStore();
  const [searchOrganization, setSearchOrganization] = useState<string>('');
  const {
    data: allOrganizations,
    fetchNextPage,
    hasNextPage
  } = useGetAllOrganizations(PAGE_SIZE, searchOrganization);
  const getOrganizationOptions = () => {
    return allOrganizations?.pages.flatMap((page) =>
      page.data?.items.map((organization: Organization) => ({
        value: organization.id,
        label: organization.name
      }))
    ) as SelectOptionProps[];
  };

  return (
    <Formik
      initialValues={{
        filterByOnlineStatus: filterByOnlineStatus ?? null,
        filterByEnabledStatus: filterByEnabledStatus ?? null,
        filterByOrganization: filterByOrganization ?? null
      }}
      onSubmit={() => undefined}>
      {({ setFieldValue, values }) => (
        <Form>
          <FilterModalLayout
            apply={apply as (values: FilterPopUpValues) => void}
            reset={reset}
            onClose={onClose}
            values={values}>
            <SingleSelect
              placeholder={t('selectStatus')}
              isClearable
              label={t('status')}
              id='filterByOnlineStatus'
              options={getOnlineStatusFilterOptions()}
              handleChange={(value) =>
                setFieldValue('filterByOnlineStatus', value as SelectOptionProps)
              }
              value={values.filterByOnlineStatus}
            />
            <SingleSelect
              placeholder={t('selectAvailability')}
              isClearable
              label={t('availability')}
              id='filterByEnabledStatus'
              options={getEnabledStatusFilterOptions()}
              handleChange={(value) =>
                setFieldValue('filterByEnabledStatus', value as SelectOptionProps)
              }
              value={values.filterByEnabledStatus}
            />
            {currentUser?.organizations.length === 1 || organizationFilter ? null : (
              <SingleSelect
                placeholder={t('selectOrganization')}
                isClearable
                label={t('organization')}
                id='filterByOrganization'
                options={
                  currentUser?.organizations && currentUser.organizations.length >= 2
                    ? currentUser?.organizations?.map((orgs: Organization) => ({
                        value: orgs.id,
                        label: orgs.name
                      }))
                    : getOrganizationOptions()
                }
                handleChange={(value) =>
                  setFieldValue('filterByOrganization', value as SelectOptionProps)
                }
                value={values.filterByOrganization}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isSearchable={true}
                handleInputChange={(inputValue: string) => setSearchOrganization(inputValue)}
              />
            )}
          </FilterModalLayout>
        </Form>
      )}
    </Formik>
  );
};
